import { Injectable, computed, inject, signal } from '@angular/core';
import { finalize, forkJoin } from 'rxjs';
import { CompanyEarningsService } from '../services/company-earnings.service';
import { DateAggregation, DatePeriod } from 'src/app/shared/models/chart.model';
import { CostCenterTurnover } from '../models/company-earnings.model';
import { TimeSeriesResponseDto } from 'src/app/shared/models/time-series-response.model';
import {
  endOfDay,
  endOfMonth,
  endOfWeekSunday,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeekMonday,
  startOfYear,
} from 'src/app/shared/utils/date.utils';
import { TreeNode } from 'primeng/api';
import { mapCostCenterTurnoversToTreeNodes } from '../mappers/cost-center-turnover-to-tee-node.mapper';
import {
  LoadingService,
  OVERLAY_POSITION,
} from 'src/app/shared/services/loading.service';
import { UserStore } from 'src/app/shared/user/store/user.store';
import { TurnoverByCostCenterItemDto } from '../services/company-earnings.service';

export interface CompanyEarningsState {
  range: DateAggregation;
  period: DatePeriod;
  dateFrom?: Date;
  dateTo?: Date;
  data: TimeSeriesResponseDto | null;
  costCenterTurnovers: CostCenterTurnover[];
  costCenterTurnoverNodes: TreeNode[] | undefined;
}

@Injectable({ providedIn: 'root' })
export class CompanyEarningsStore {
  loadingService = inject(LoadingService);
  userStore = inject(UserStore);

  private readonly _state = signal<CompanyEarningsState>({
    range: DateAggregation.Year,
    period: DatePeriod.ThirteenMonths,
    data: null,
    costCenterTurnovers: [],
    costCenterTurnoverNodes: undefined,
  });

  readonly state = this._state.asReadonly();

  readonly range = computed(() => this.state().range);
  readonly period = computed(() => this.state().period);
  readonly data = computed(() => this.state().data);
  readonly costCenterTurnovers = computed(
    () => this.state().costCenterTurnovers,
  );
  readonly costCenterTurnoverNodes = computed(
    () => this.state().costCenterTurnoverNodes,
  );

  constructor(private readonly service: CompanyEarningsService) {}

  setPeriod(period: DatePeriod) {
    this._state.update((s) => ({ ...s, period }));
  }

  load(period: DatePeriod) {
    let dateFrom = new Date();
    let dateTo = new Date();
    const now = new Date();
    switch (period) {
      case DatePeriod.FourteenDays: {
        // 14 Tage inkl. heute => heute + 13 Tage zurück
        dateTo = endOfDay(now);
        dateFrom = startOfDay(now);
        dateFrom.setDate(dateFrom.getDate() - 13);
        this._state.update((s) => ({
          ...s,
          dateFrom,
          dateTo,
          range: DateAggregation.Day,
        }));
        break;
      }

      case DatePeriod.TenWeeks: {
        // 10 Wochen inkl. aktueller Woche
        dateFrom = startOfWeekMonday(now);
        dateFrom.setDate(dateFrom.getDate() - 9 * 7); // 9 Wochen zurück + aktuelle = 10
        dateTo = endOfWeekSunday(now);
        this._state.update((s) => ({
          ...s,
          dateFrom,
          dateTo,
          range: DateAggregation.Week,
        }));
        break;
      }

      case DatePeriod.ThirteenMonths: {
        // 13 Monate inkl. aktueller Monat
        dateFrom = startOfMonth(now);
        dateFrom.setMonth(dateFrom.getMonth() - 12); // 12 Monate zurück + aktueller = 13
        dateTo = endOfMonth(now);
        this._state.update((s) => ({
          ...s,
          dateFrom,
          dateTo,
          range: DateAggregation.Month,
        }));
        break;
      }

      case DatePeriod.EightQuarter: {
        // 8 Quartale = 4 Vorjahr + 4 aktuelles Jahr
        const y = now.getFullYear();
        dateFrom = startOfYear(y - 1);
        dateTo = endOfYear(y);
        this._state.update((s) => ({
          ...s,
          dateFrom,
          dateTo,
          range: DateAggregation.Quarter,
        }));
        break;
      }

      case DatePeriod.AllYears: {
        // von 1900 bis Ende aktuelles Jahr
        const y = now.getFullYear();
        dateFrom = startOfYear(y - 12);
        dateTo = endOfYear(y);
        this._state.update((s) => ({
          ...s,
          dateFrom,
          dateTo,
          range: DateAggregation.Year,
        }));
        break;
      }

      default: {
        // falls du "heute" als default willst
        dateFrom = startOfDay(now);
        dateTo = endOfDay(now);
      }
    }
    this.loadingService.show(OVERLAY_POSITION.COMPONENT);

    const params = {
      dateFrom: this.state().dateFrom ?? new Date(),
      dateTo: this.state().dateTo ?? new Date(),
      aggregateBy: this.state().range,
    };

    forkJoin({
      chartData: this.service.getCompanyTurnover(params),
      costCenterData: this.service.getCostCenterTurnover({
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        includeChildren: true, // <-- zum Ermitteln ob Kinder vorhanden sind
      }),
    })
      .pipe(
        finalize(() => this.loadingService.hide(OVERLAY_POSITION.COMPONENT)),
      )
      .subscribe({
        next: ({ chartData, costCenterData }) => {
          const turnovers = this.mapDtoToTurnovers(costCenterData.items);
          const nodes = mapCostCenterTurnoversToTreeNodes(turnovers, {
            expandAll: false,
          }).map((node) => ({
            ...node,
            // leaf bleibt vom Mapper (true wenn keine Kinder laut API, false wenn Kinder vorhanden)
            children: undefined, // Kinder werden lazy beim Aufklappen geladen
          }));

          this._state.update((s) => ({
            ...s,
            data: chartData,
            costCenterTurnovers: turnovers,
            costCenterTurnoverNodes: nodes,
          }));
        },
        error: (err) => {
          this.loadingService.hide(OVERLAY_POSITION.COMPONENT);
          throw err;
        },
      });
  }

  private mapDtoToTurnovers(
    items: TurnoverByCostCenterItemDto[],
  ): CostCenterTurnover[] {
    return items.map((item) => ({
      costCenterId: String(item.costCenterId),
      name: item.name,
      actualSales: Number(item.actualSales),
      targetSales: Number(item.targetSales),
      difference: Number(item.difference),
      children: item.children?.length
        ? this.mapDtoToTurnovers(item.children)
        : null,
    }));
  }

  loadChildren(node: TreeNode): void {
    const costCenterId = node.data?.costCenterId;
    if (!costCenterId) return;

    const params = this.state();
    const dateFrom = params.dateFrom ?? new Date();
    const dateTo = params.dateTo ?? new Date();

    node.loading = true;

    this.service
      .getCostCenterTurnover({
        dateFrom,
        dateTo,
        costCenterIds: costCenterId,
        includeChildren: true,
      })
      .pipe(
        finalize(() => {
          node.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          const parentItem = response.items.find(
            (i) => String(i.costCenterId) === String(costCenterId),
          );
          const childItems = parentItem?.children ?? [];
          const childNodes = mapCostCenterTurnoversToTreeNodes(
            this.mapDtoToTurnovers(childItems),
            { expandAll: false },
          ).map((child) => ({ ...child, leaf: !child.children?.length }));

          node.children = childNodes.length ? childNodes : [];
          node.leaf = childNodes.length === 0;
          node.data = { ...node.data, childrenLoaded: true };

          // Trigger change detection by updating the nodes reference
          this._state.update((s) => ({
            ...s,
            costCenterTurnoverNodes: s.costCenterTurnoverNodes
              ? [...s.costCenterTurnoverNodes]
              : [],
          }));
        },
        error: (err) => {
          node.children = [];
          node.leaf = true;
          node.data = { ...node.data, childrenLoaded: true };
          throw err;
        },
      });
  }
}
